<template>
    <div class="provisioning-page">
        <section class="provisioning-hero py-5">
            <div class="container py-lg-5">
                <div class="row align-items-center gy-5">
                    <div class="col-12 col-lg-6">
                        <p class="section-eyebrow">{{ $t('marketing.provisioning.eyebrow') }}</p>
                        <h1 class="display-4 fw-bolder text-gray-900 mb-4">{{ $t('marketing.provisioning.title') }}</h1>
                        <p class="fs-5 text-gray-600 hero-copy mb-4">{{ $t('marketing.provisioning.subtitle') }}</p>
                        <div class="d-flex flex-column flex-sm-row gap-3">
                            <router-link to="/login" class="btn btn-brand btn-lg text-white fw-bold px-4 py-3">
                                {{ $t('marketing.provisioning.primary_cta') }}
                            </router-link>
                            <router-link :to="{ name: 'contact' }" class="btn btn-outline-brand btn-lg fw-bold px-4 py-3">
                                {{ $t('marketing.provisioning.secondary_cta') }}
                            </router-link>
                        </div>
                        <p class="small text-gray-500 mt-3 mb-0">{{ $t('marketing.cta.reassurance') }}</p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <div class="device-preview">
                            <img class="w-100" :src="'/img/bank/' + locale + '_tapgiant-tablet-requisition.png'" :alt="$t('marketing.provisioning.title')">
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5 text-center">
            <p class="section-eyebrow">{{ $t('marketing.provisioning.trusted') }}</p>
            <div class="d-flex flex-wrap justify-content-center align-items-center gap-5 mt-4">
                <img class="customer-logo" src="/img/testimonials/noir-coffee.jpg" alt="NOIR COFFEE ROASTERS">
                <img class="customer-logo customer-logo--wide" src="/img/testimonials/logo-notre-patisserie.svg" alt="NOTRE PATISSERIE">
            </div>
        </section>

        <section class="pain-band py-5">
            <div class="container py-lg-4 text-center">
                <h2 class="display-6 fw-bolder text-gray-900 mx-auto">{{ $t('marketing.provisioning.pain_title') }}</h2>
                <p class="fs-5 text-gray-600 mx-auto mb-0">{{ $t('marketing.provisioning.pain_subtitle') }}</p>
            </div>
        </section>

        <section class="container py-5">
            <div class="text-center mb-5">
                <p class="section-eyebrow">{{ $t('marketing.home.workflow.eyebrow') }}</p>
                <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.provisioning.workflow_title') }}</h2>
            </div>
            <div class="row g-4">
                <div v-for="(step, index) in steps" :key="step.title" class="col-12 col-lg-4">
                    <div class="step-card h-100 p-4 p-md-5">
                        <div class="step-number mb-4">{{ index + 1 }}</div>
                        <h3 class="h4 fw-bolder">{{ step.title }}</h3>
                        <p class="text-gray-600 mb-0">{{ step.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <div class="row align-items-center gy-5">
                <div class="col-12 col-lg-6">
                    <div class="phone-pair text-center">
                        <img :src="'/img/bank/' + locale + '_tapgiant-mobile-order-dashboard.png'" :alt="$t('marketing.provisioning.steps.2.title')">
                        <img :src="'/img/bank/' + locale + '_tapgiant-mobile-order-summary.png'" :alt="$t('marketing.provisioning.steps.2.title')">
                    </div>
                </div>
                <div class="col-12 col-lg-6">
                    <h2 class="display-6 fw-bolder text-gray-900 mb-4">{{ $t('marketing.provisioning.benefits_title') }}</h2>
                    <div v-for="benefit in benefits" :key="benefit.title" class="d-flex gap-3 mb-4">
                        <BootstrapIcon class="flex-shrink-0 mt-1" color="fill-teal-600 text-teal-600" name="check-circle-fill" :size="21" />
                        <div>
                            <h3 class="h5 fw-bolder mb-1">{{ benefit.title }}</h3>
                            <p class="text-gray-600 mb-0">{{ benefit.description }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="fit-section py-5">
            <div class="container py-lg-4">
                <h2 class="display-6 fw-bolder text-gray-900 text-center mb-5">{{ $t('marketing.provisioning.fit_title') }}</h2>
                <div class="row g-3 justify-content-center">
                    <div v-for="(item, index) in fit" :key="item" class="col-12 col-sm-6 col-lg-3">
                        <div class="fit-card h-100 text-center p-4">
                            <BootstrapIcon class="mb-3" color="fill-teal-700 text-teal-700" :name="fitIcons[index]" :size="30" />
                            <p class="fw-bolder mb-0">{{ item }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <figure class="proof-card text-center mx-auto p-4 p-md-5">
                <img class="customer-logo mb-4" src="/img/testimonials/noir-coffee.jpg" alt="NOIR COFFEE ROASTERS">
                <blockquote class="fs-4 text-gray-700">“{{ $t('marketing.provisioning.proof_quote') }}”</blockquote>
                <figcaption class="small fw-bold text-gray-500">{{ $t('marketing.provisioning.proof_author') }}</figcaption>
            </figure>
        </section>

        <section class="container py-5">
            <div class="final-cta text-center p-4 p-md-5">
                <h2 class="display-6 fw-bolder text-white mx-auto">{{ $t('marketing.provisioning.final_title') }}</h2>
                <p class="fs-5 text-white-50 mx-auto mb-4">{{ $t('marketing.provisioning.final_subtitle') }}</p>
                <router-link to="/login" class="btn btn-light btn-lg px-5 py-3 fw-bold text-teal-800">
                    {{ $t('marketing.provisioning.primary_cta') }}
                </router-link>
            </div>
        </section>
    </div>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'

export default {
    components: { BootstrapIcon },
    data: function () {
        return { fitIcons: ['cake2', 'cup-hot', 'shop', 'boxes'] }
    },
    computed: {
        locale: function () {
            return this.$i18n.locale
        },
        steps: function () {
            return [1, 2, 3].map(index => ({
                title: this.$t(`marketing.provisioning.steps.${index}.title`),
                description: this.$t(`marketing.provisioning.steps.${index}.description`)
            }))
        },
        benefits: function () {
            return [1, 2, 3, 4].map(index => ({
                title: this.$t(`marketing.provisioning.benefits.${index}.title`),
                description: this.$t(`marketing.provisioning.benefits.${index}.description`)
            }))
        },
        fit: function () {
            return [1, 2, 3, 4].map(index => this.$t(`marketing.provisioning.fit.${index}`))
        }
    }
}
</script>

<style scoped lang="scss">
.provisioning-hero {
    background: linear-gradient(145deg, #fff 0%, #effaf7 58%, #fff 100%);
    margin-top: -30px;
}

.section-eyebrow {
    color: #16856a;
    font-size: .78rem;
    font-weight: 800;
    letter-spacing: .12em;
    text-transform: uppercase;
}

.hero-copy { line-height: 1.7; }

.device-preview {
    filter: drop-shadow(0 25px 35px rgba(35, 61, 55, .18));
}

.customer-logo {
    max-height: 82px;
    max-width: 120px;
    object-fit: contain;
}

.customer-logo--wide { max-width: 165px; }

.pain-band,
.fit-section { background: #f7faf9; }

.pain-band h2,
.pain-band p { max-width: 850px; }

.step-card,
.fit-card,
.proof-card {
    background: #fff;
    border: 1px solid #e5ecea;
    border-radius: 18px;
}

.step-number {
    align-items: center;
    background: #d9f7ee;
    border-radius: 50%;
    color: #126d58;
    display: flex;
    font-size: 1.1rem;
    font-weight: 800;
    height: 44px;
    justify-content: center;
    width: 44px;
}

.phone-pair img {
    filter: drop-shadow(0 18px 20px rgba(35, 61, 55, .14));
    max-width: 45%;
}

.proof-card { max-width: 850px; }

.final-cta {
    background: linear-gradient(135deg, #12755e 0%, #20a982 100%);
    border-radius: 24px;
}

.final-cta h2,
.final-cta p { max-width: 800px; }

@media (max-width: 767.98px) {
    .provisioning-hero .display-4 { font-size: 2.4rem; }
}
</style>
